import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../services/api";
import { AppShell, TopNav, BodyGrid, PageBody } from "../components/Layout";

/* ── Modal for "Make my business better" ── */
function ExistingBusinessModal({ onClose, onSubmit }) {
  const [website, setWebsite]     = useState("");
  const [challenge, setChallenge] = useState("");
  const [error, setError]         = useState("");

  function isValidUrl(val) {
    try { new URL(val.startsWith("http") ? val : `https://${val}`); return true; }
    catch { return false; }
  }

  function handleSubmit() {
    const cleaned = website.trim();
    if (!cleaned) { setError("Please enter your website URL."); return; }
    if (!isValidUrl(cleaned)) { setError("That doesn't look like a valid URL. Try e.g. https://mybusiness.com"); return; }
    const normalized = cleaned.startsWith("http") ? cleaned : `https://${cleaned}`;
    onSubmit(normalized, challenge.trim());
  }

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.modal} onClick={e => e.stopPropagation()}>
        <div style={s.modalHeader}>
          <h2 style={s.modalTitle}>Tell us about your business</h2>
          <button style={s.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div style={s.field}>
          <label style={s.label}>Business website <span style={s.required}>*</span></label>
          <p style={s.hint}>Your AI CEO will analyse your site to understand your positioning, market, and gaps.</p>
          <input
            style={{ ...s.input, ...(error && !website ? s.inputError : {}) }}
            type="text"
            placeholder="https://mybusiness.com"
            value={website}
            onChange={e => { setWebsite(e.target.value); setError(""); }}
          />
          {error && <p style={s.errorInline}>{error}</p>}
        </div>

        <div style={s.field}>
          <label style={s.label}>Biggest challenge <span style={s.optional}>optional</span></label>
          <p style={s.hint}>What's the one thing holding your business back? e.g. "Can't get past $10k/mo" or "Struggling with customer retention."</p>
          <textarea
            style={s.textarea}
            placeholder="e.g. We have great reviews but can't seem to grow beyond our local area."
            value={challenge}
            onChange={e => setChallenge(e.target.value)}
            rows={3}
          />
        </div>

        <div style={s.modalActions}>
          <button style={s.ghostBtn} onClick={onClose}>Cancel</button>
          <button style={s.submitBtn} onClick={handleSubmit}>
            Analyse my business →
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Main page ── */
export default function OnboardingLandingPage() {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading]     = useState(null); // 'new' | 'existing' | 'surprise'

  async function handleNew() {
    navigate("/onboarding/idea?mode=new");
  }

async function handleExisting(website, challenge) {
  // ← removed setShowModal(false) from here
  setLoading("existing");
  try {
    const ws = await apiFetch("/api/workspaces", {
      method: "POST",
      body: JSON.stringify({ mode: "existing", website, challenge: challenge || undefined }),
    });
    setShowModal(false);  // ← only close on success
    navigate(`/onboarding/stream?workspace_id=${ws.id}&mode=existing&auto_run=false`);
  } catch (e) {
    setLoading(null);
    alert(e.message || "Something went wrong. Please try again.");
    // modal stays open
  }
}

  async function handleSurprise() {
    setLoading("surprise");
    try {
      const ws = await apiFetch("/api/workspaces", {
        method: "POST",
        body: JSON.stringify({ mode: "surprise" }),
      });
      navigate(`/onboarding/stream?workspace_id=${ws.id}&mode=surprise&auto_run=false`);
    } catch (e) {
      setLoading(null);
      alert(e.message || "Something went wrong. Please try again.");
    }
  }

  return (
    <AppShell>
      <TopNav />
      <BodyGrid>
        <PageBody>

          <div style={s.pageHeader}>
            <h1 style={s.pageTitle}>What would you like to do?</h1>
            <p style={s.pageSubtitle}>
              Your AI CEO will build a full strategy, delegate tasks to your agent team,
              and get to work — overnight.
            </p>
          </div>

          <div style={s.cardGrid}>

            {/* ── Option 1: New business ── */}
            <button
              style={{ ...s.card, ...(loading === "new" ? s.cardLoading : {}) }}
              onClick={handleNew}
              disabled={!!loading}
            >
              <span style={s.cardIcon}>🚀</span>
              <span style={s.cardTitle}>Start a new business</span>
              <span style={s.cardDesc}>
                Describe your idea and your AI CEO will produce a market analysis,
                strategy, and first sprint — ready by morning.
              </span>
              <span style={s.cardArrow}>→</span>
            </button>

            {/* ── Option 2: Existing business ── */}
            <button
              style={{ ...s.card, ...(loading === "existing" ? s.cardLoading : {}) }}
              onClick={() => setShowModal(true)}
              disabled={!!loading}
            >
              <span style={s.cardIcon}>📈</span>
              <span style={s.cardTitle}>Grow my existing business</span>
              <span style={s.cardDesc}>
                Share your website and biggest challenge. Your AI CEO will audit
                your positioning and build a growth plan.
              </span>
              <span style={s.cardArrow}>→</span>
            </button>

            {/* ── Option 3: Surprise me ── */}
            <button
              style={{ ...s.card, ...s.cardSurprise, ...(loading === "surprise" ? s.cardLoading : {}) }}
              onClick={handleSurprise}
              disabled={!!loading}
            >
              {loading === "surprise"
                ? <span style={s.cardIcon}>⏳</span>
                : <span style={s.cardIcon}>🎲</span>}
              <span style={s.cardTitle}>Surprise me</span>
              <span style={s.cardDesc}>
                Let your AI CEO research your background and recommend a business
                idea tailored to your skills and interests.
              </span>
              <span style={s.cardArrow}>→</span>
            </button>

          </div>

          {loading && (
            <p style={s.loadingNote}>
              {loading === "surprise"
                ? "Researching your background — this may take a moment…"
                : "Setting up your workspace…"}
            </p>
          )}

        </PageBody>
      </BodyGrid>

      {showModal && (
        <ExistingBusinessModal
          onClose={() => setShowModal(false)}
          onSubmit={handleExisting}
        />
      )}
    </AppShell>
  );
}

/* ─────────────────────────────────────────────
   STYLES
   ───────────────────────────────────────────── */
const s = {
  pageHeader: {
    marginBottom: 32,
  },
  pageTitle: {
    fontFamily: "var(--serif)",
    fontSize: 26,
    fontWeight: 400,
    color: "var(--text)",
    marginBottom: 6,
  },
  pageSubtitle: {
    fontSize: 14,
    color: "var(--text-soft)",
    lineHeight: 1.6,
    maxWidth: 520,
  },

  /* ── Card grid ── */
  cardGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: 16,
    marginBottom: 24,
  },
  card: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 10,
    padding: "28px 24px",
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: "var(--r)",
    cursor: "pointer",
    textAlign: "left",
    transition: "border-color 0.15s, background 0.15s",
    position: "relative",
  },
  cardLoading: {
    opacity: 0.6,
    cursor: "not-allowed",
  },
  cardSurprise: {
    borderStyle: "dashed",
  },
  cardIcon: {
    fontSize: 28,
    lineHeight: 1,
  },
  cardTitle: {
    fontFamily: "var(--serif)",
    fontSize: 17,
    fontWeight: 400,
    color: "var(--text)",
    lineHeight: 1.3,
  },
  cardDesc: {
    fontSize: 13,
    color: "var(--text-soft)",
    lineHeight: 1.6,
    flex: 1,
  },
  cardArrow: {
    fontSize: 16,
    color: "var(--accent)",
    marginTop: 4,
  },

  loadingNote: {
    fontSize: 13,
    color: "var(--text-mute)",
    fontStyle: "italic",
  },

  /* ── Modal overlay ── */
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: 16,
  },
  modal: {
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: "var(--r)",
    padding: 28,
    width: "100%",
    maxWidth: 520,
    display: "flex",
    flexDirection: "column",
    gap: 18,
  },
  modalHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  modalTitle: {
    fontFamily: "var(--serif)",
    fontSize: 18,
    fontWeight: 400,
    color: "var(--text)",
    margin: 0,
  },
  closeBtn: {
    background: "none",
    border: "none",
    fontSize: 16,
    color: "var(--text-mute)",
    cursor: "pointer",
    padding: "4px 8px",
  },

  /* ── Form fields (reused from original page) ── */
  field: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  label: {
    fontSize: 13,
    fontWeight: 500,
    color: "var(--text)",
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  optional: {
    fontSize: 11,
    fontWeight: 400,
    color: "var(--text-mute)",
  },
  required: {
    color: "var(--accent)",
    fontWeight: 400,
  },
  hint: {
    fontSize: 12,
    color: "var(--text-mute)",
    lineHeight: 1.5,
    margin: 0,
  },
  input: {
    width: "100%",
    padding: "9px 12px",
    fontSize: 13,
    fontFamily: "var(--sans)",
    color: "var(--text)",
    background: "var(--bg)",
    border: "1px solid var(--border)",
    borderRadius: "var(--r)",
    outline: "none",
    boxSizing: "border-box",
  },
  inputError: {
    borderColor: "var(--accent)",
  },
  errorInline: {
    fontSize: 12,
    color: "var(--red, #c0392b)",
    margin: 0,
  },
  textarea: {
    width: "100%",
    padding: "10px 12px",
    fontSize: 13,
    fontFamily: "var(--sans)",
    color: "var(--text)",
    background: "var(--bg)",
    border: "1px solid var(--border)",
    borderRadius: "var(--r)",
    outline: "none",
    resize: "vertical",
    lineHeight: 1.6,
    boxSizing: "border-box",
  },

  /* ── Modal actions ── */
  modalActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    paddingTop: 4,
  },
  ghostBtn: {
    padding: "9px 18px",
    background: "none",
    border: "1px solid var(--border)",
    borderRadius: "var(--r)",
    fontSize: 13,
    color: "var(--text-soft)",
    cursor: "pointer",
  },
  submitBtn: {
    padding: "9px 20px",
    background: "var(--accent)",
    color: "#fff",
    border: "none",
    borderRadius: "var(--r)",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
  },
};